import { useEffect, useRef, useState } from "react";
import { Star, Check, X, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { fetchReviews, submitReview, uploadReviewPhoto, reviewPhotoUrl } from "@/lib/api";

const Stars = ({ rating, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${rating}/5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        strokeWidth={1.4}
        className={i <= rating ? "fill-[#D4A24A] text-[#D4A24A]" : "text-[#dcd7ce]"}
      />
    ))}
  </span>
);

const ReviewForm = ({ productId, onClose, onSubmitted }) => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [purchase, setPurchase] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|heic)$/i.test(f.type)) {
      toast.error(isFr ? "Formats acceptés : JPG, PNG, WEBP" : "Accepted: JPG, PNG, WEBP");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error(isFr ? "Photo trop volumineuse (max 8 Mo)" : "Photo too large (max 8 MB)");
      return;
    }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result || null);
    reader.readAsDataURL(f);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (text.length < 20) {
      toast.error(isFr ? "L'avis doit faire au moins 20 caractères" : "Review must be at least 20 characters");
      return;
    }
    if (!purchase) {
      toast.error(isFr ? "Veuillez certifier votre achat" : "Please certify your purchase");
      return;
    }
    setLoading(true);
    try {
      let photo_url = null;
      if (photoFile) {
        setUploading(true);
        const up = await uploadReviewPhoto(photoFile);
        photo_url = up?.photo_url || null;
        setUploading(false);
      }
      await submitReview({
        product_id: productId,
        name,
        email,
        rating,
        text,
        purchase_confirmed: purchase,
        photo_url,
        lang,
      });
      onSubmitted();
    } catch (err) {
      setUploading(false);
      toast.error(err?.response?.data?.detail || (isFr ? "Erreur lors de l'envoi" : "Submission error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto" role="dialog" aria-modal="true">
      <button type="button" onClick={onClose} aria-label="Close" className="absolute inset-0 bg-[#1A1A1A]/45" />
      <form
        onSubmit={submit}
        data-testid="review-form"
        className="relative bg-[#F5F0EB] max-w-md w-full p-7 md:p-9 rounded-md shadow-2xl my-auto"
      >
        <button type="button" onClick={onClose} className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center text-[#3D2B1F]" data-testid="review-close">
          <X size={16} />
        </button>
        <div className="eyebrow mb-2 text-[#8B6343]">{isFr ? "Votre avis" : "Your review"}</div>
        <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mb-5 leading-tight">
          {isFr ? "Partagez votre expérience" : "Share your experience"}
        </h2>

        <label className="block text-xs mb-2" style={{ color: "#8B6343", letterSpacing: ".18em", textTransform: "uppercase" }}>
          {isFr ? "Votre note" : "Your rating"}
        </label>
        <div className="flex gap-1 mb-4" data-testid="review-rating">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setRating(i)}
              aria-label={`${i} stars`}
              data-testid={`review-rating-${i}`}
            >
              <Star
                size={28}
                strokeWidth={1.4}
                className={i <= rating ? "fill-[#D4A24A] text-[#D4A24A]" : "text-[#dcd7ce]"}
              />
            </button>
          ))}
        </div>

        <input
          type="text"
          required
          minLength={2}
          maxLength={80}
          placeholder={isFr ? "Prénom + initiale du nom (ex. Camille L.)" : "First name + initial (e.g. Camille L.)"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="review-name"
          className="w-full bg-transparent border-b border-[#3D2B1F]/30 focus:border-[#3D2B1F] outline-none py-3 text-[15px] mb-3 text-[#3D2B1F]"
        />
        <input
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="review-email"
          className="w-full bg-transparent border-b border-[#3D2B1F]/30 focus:border-[#3D2B1F] outline-none py-3 text-[15px] mb-3 text-[#3D2B1F]"
        />
        <textarea
          required
          minLength={20}
          maxLength={500}
          rows={4}
          placeholder={isFr ? "Votre avis (20–500 caractères)" : "Your review (20–500 chars)"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="review-text"
          className="w-full bg-transparent border border-[#3D2B1F]/30 focus:border-[#3D2B1F] outline-none p-3 text-[15px] mb-2 text-[#3D2B1F] rounded-sm resize-none"
        />
        <div className="text-xs text-[#8a8378] text-right mb-4">{text.length}/500</div>

        {/* Photo (optional) */}
        <div className="mb-5">
          <label className="block text-xs mb-2" style={{ color: "#8B6343", letterSpacing: ".18em", textTransform: "uppercase" }}>
            {isFr ? "Photo (optionnel)" : "Photo (optional)"}
          </label>
          {photoPreview ? (
            <div className="relative inline-block">
              <img
                src={photoPreview}
                alt="preview"
                className="w-28 h-28 object-cover rounded-sm border border-[#dcd7ce]"
                data-testid="review-photo-preview"
              />
              <button
                type="button"
                onClick={clearPhoto}
                aria-label="Remove photo"
                data-testid="review-photo-remove"
                className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#3D2B1F] text-white inline-flex items-center justify-center"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="review-photo-button"
              className="inline-flex items-center gap-2 border border-dashed border-[#3D2B1F]/40 text-[#3D2B1F] px-4 py-3 rounded-sm hover:bg-white text-sm"
            >
              <Camera size={16} />
              {isFr ? "Ajouter une photo" : "Add a photo"}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            onChange={onPickPhoto}
            className="hidden"
            data-testid="review-photo-input"
          />
          <p className="text-[11px] text-[#8a8378] mt-2">
            {isFr ? "JPG, PNG ou WEBP — 8 Mo max. Photos modérées avant publication." : "JPG, PNG or WEBP — 8 MB max. Photos are moderated before publishing."}
          </p>
        </div>

        <label className="flex items-start gap-3 cursor-pointer mb-5">
          <input
            type="checkbox"
            checked={purchase}
            onChange={(e) => setPurchase(e.target.checked)}
            required
            data-testid="review-certify"
            className="mt-1"
          />
          <span className="text-xs text-[#3D2B1F] leading-relaxed">
            {isFr ? "Je certifie avoir acheté ce produit sur myflovea.com" : "I certify I purchased this product on myflovea.com"}
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          data-testid="review-submit"
          className="w-full bg-[#3D2B1F] text-white py-4 text-xs tracking-[0.22em] uppercase rounded-md hover:bg-[#1A1A1A] disabled:opacity-60"
        >
          {loading
            ? (uploading ? (isFr ? "Upload photo…" : "Uploading…") : (isFr ? "Envoi…" : "Sending…"))
            : (isFr ? "Envoyer mon avis" : "Submit my review")}
        </button>
      </form>
    </div>
  );
};

const Reviews = ({ productId }) => {
  const { lang } = useLang();
  const isFr = lang === "fr";
  const [data, setData] = useState({ items: [], average: null, count: 0 });
  const [showForm, setShowForm] = useState(false);
  const [thanks, setThanks] = useState(false);

  const load = () => fetchReviews(productId).then(setData).catch(() => {});
  useEffect(() => { load(); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section id="reviews" data-testid="reviews-dynamic-section" className="border-t border-[#dcd7ce] pt-12 mt-16">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2 text-[#8B6343]">{isFr ? "Avis vérifiés clients" : "Verified customer reviews"}</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A] leading-tight">
            {isFr ? "Partagez votre expérience" : "Share your experience"}
          </h2>
          {data.count > 0 && (
            <div className="mt-3 flex items-center gap-3 text-sm" data-testid="reviews-average">
              <Stars rating={Math.round(data.average || 0)} />
              <span className="text-[#3D2B1F]">
                <strong>{(data.average || 0).toFixed(1)}/5</strong>{" "}
                <span className="text-[#8a8378]">— {isFr ? `basé sur ${data.count} avis` : `based on ${data.count} reviews`}</span>
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="open-review-form"
          className="self-start bg-[#3D2B1F] text-white px-5 py-3 text-xs tracking-[0.22em] uppercase rounded-md hover:bg-[#1A1A1A]"
        >
          {isFr ? "Laisser un avis" : "Leave a review"}
        </button>
      </div>

      {thanks && (
        <div data-testid="review-thanks" className="bg-[#F5EFE6] border-l-3 border-[#8B6343] p-5 mb-8 rounded-sm flex items-start gap-3">
          <Check size={18} className="text-[#3D2B1F] mt-0.5" strokeWidth={1.6} />
          <p className="text-sm text-[#3D2B1F]">
            {isFr
              ? "Merci ! Votre avis est en cours de vérification. Il sera publié sous 48h."
              : "Thank you! Your review is being verified. It will be published within 48h."}
          </p>
        </div>
      )}

      {data.items.length > 0 && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.items.map((r) => (
            <li
              key={r.id}
              data-testid={`review-dynamic-${r.id}`}
              className="bg-white border border-[#dcd7ce] rounded-md p-5"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[#3D2B1F] font-medium text-sm">{r.name}</div>
                <span className="text-[10px] tracking-[0.14em] uppercase bg-[#F5EFE6] text-[#3D2B1F] px-2 py-1 rounded-full">
                  {isFr ? "Avis vérifié ✓" : "Verified ✓"}
                </span>
              </div>
              <Stars rating={r.rating} />
              {r.photo_url && (
                <img
                  src={reviewPhotoUrl(r.photo_url)}
                  alt="customer photo"
                  loading="lazy"
                  className="mt-3 w-full max-h-72 object-cover rounded-sm border border-[#dcd7ce]"
                  data-testid={`review-photo-${r.id}`}
                />
              )}
              <p className="mt-3 text-[14px] text-[#3D2B1F] leading-relaxed">&ldquo;{r.text}&rdquo;</p>
              <div className="mt-3 text-[11px] text-[#8a8378]">{(r.approved_at || r.created_at || "").slice(0, 10)}</div>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <ReviewForm
          productId={productId}
          onClose={() => setShowForm(false)}
          onSubmitted={() => { setShowForm(false); setThanks(true); load(); }}
        />
      )}
    </section>
  );
};

export default Reviews;
